// src/pages/Menu/Articulacion/Minutes/Minutes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  listMinutes,
  createMinute,
  deleteMinute,
  updateMinute,
} from "../../../../services/minutesService";
import { listEntitiesForSuggestions } from "../../../../services/entitiesLookupService";
import { Banner } from "../../../../components/common/Banner";
import SectionTips from "../../../../components/common/SectionTips";
import TooltipHelper from "../../../../components/common/TooltipHelper";
import { Eye, FileText, MapPin, CalendarDays, Info, Plus, Filter } from "lucide-react";
import ArrowBack from "../../../../components/common/ArrowBack";
import { Button } from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";

/* =============== util admin & helpers =============== */
function useIsAdmin() {
  const token =
    localStorage.getItem("token") || localStorage.getItem("accessToken");
  if (!token) return false;
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (payload?.isAdmin) return true;
    const roles = payload?.roles || payload?.role || payload?.authorities || [];
    const arr = Array.isArray(roles) ? roles : [roles];
    return arr.some((r) => String(r).toUpperCase().includes("ADMIN"));
  } catch {
    return false;
  }
}

const norm = (s) => String(s ?? "").trim().toLowerCase();

const Label = ({ children, className = "" }) => (
  <label className={`block text-xs text-gray-600 ${className}`}>
    {children}
  </label>
);


/* ---------- Helpers de fecha ---------- */
const isYMD = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));

const toYMDForInput = (v) => {
  if (!v) return "";
  if (isYMD(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(+d)) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDateSafe = (v) => {
  if (!v) return "";
  if (isYMD(v)) {
    const [, y, m, d] = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return `${d}-${m}-${y}`;
  }
  const d = new Date(v);
  if (Number.isNaN(+d)) return String(v);
  const opts = { year: "numeric", month: "2-digit", day: "2-digit" };
  const useUTC = /Z$|\+\d{2}:\d{2}$/.test(String(v));
  return d.toLocaleDateString(
    "es-CL",
    useUTC ? { ...opts, timeZone: "UTC" } : opts
  );
};

const ymdToUTCDate = (ymd) => {
  if (!isYMD(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/* ====== Marco degradado reusable ====== */
function GradientCard({ children, className = "", style }) {
  return (
    <div
      className={`rounded-2xl p-[1.5px] ${className}`}
      style={{
        background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
        ...style,
      }}
    >
      <div className="rounded-2xl bg-white ring-1 ring-black/5">
        {children}
      </div>
    </div>
  );
}

/* Consistencia de estilos (línea RDS) */
const CHIP_BASE =
  "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border";
const CHIP_ENTITY = `${CHIP_BASE} bg-indigo-50 text-indigo-800 border-indigo-100`;
const CHIP_TAG = `${CHIP_BASE} bg-gray-100 text-gray-700 border-gray-200`;
const BTN_BASE = "px-3 py-1.5 rounded-lg text-sm font-semibold";
const BTN_PRIMARY = `${BTN_BASE} bg-emerald-600 text-white hover:bg-emerald-700`;
const BTN_OUTLINE = `${BTN_BASE} bg-[#C42251] text-white hover:bg-[#A01D45]`;
const BTN_DANGER = `${BTN_BASE} bg-red-600 text-white hover:bg-red-700`;
const LOCATION_BADGE =
  "inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 border border-sky-100";

const PdfIcon = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    className="block"
    fill="none"
    aria-hidden
  >
    <path
      d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-6Z"
      stroke="#0B4A8F"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M14 2v6h6"
      stroke="#0B4A8F"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <text
      x="12"
      y="14.5"
      fontSize="7"
      fontWeight="800"
      fill="#0B4A8F"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      PDF
    </text>
  </svg>
);

/* Autocomplete filtro entidades */
function AutocompleteFilter({
  value,
  onChange,
  options = [],
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [hover, setHover] = useState(-1);
  const ref = React.useRef(null);

  useEffect(() => setQuery(value || ""), [value]);

  useEffect(() => {
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const items = useMemo(() => {
    const q = norm(query);
    const base = q ? options.filter((o) => norm(o).includes(q)) : options;
    return Array.from(new Set(base)).sort((a, b) => a.localeCompare(b));
  }, [query, options]);

  const pick = (val) => {
    onChange(val);
    setQuery(val);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHover((h) => Math.min(h + 1, items.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHover((h) => Math.max(h - 1, 0));
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (items[hover]) pick(items[hover]);
      else setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <input
        className="w-full rounded-2xl border px-4 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border bg-white shadow-lg max-h-56 overflow-auto">
          {items.map((opt, idx) => (
            <button
              key={opt}
              type="button"
              onMouseEnter={() => setHover(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt)}
              className={`w-full text-left px-3 py-2 text-sm ${
                idx === hover ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Chips de etiquetas con sugerencias (sin #) */
function ChipsAuto({
  values,
  onChange,
  suggestions = [],
  placeholder = "Añade y presiona Enter",
}) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const [hover, setHover] = useState(-1);
  const ref = React.useRef(null);

  const selected = useMemo(
    () => new Set(values.map((v) => norm(v))),
    [values]
  );

  const items = useMemo(() => {
    const q = norm(txt);
    const base = q ? suggestions.filter((s) => norm(s).includes(q)) : suggestions;
    return Array.from(new Set(base))
      .filter((s) => !selected.has(norm(s)))
      .sort((a, b) => a.localeCompare(b));
  }, [txt, suggestions, selected]);

  useEffect(() => {
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const add = (raw) => {
    const v = String(raw || "").trim();
    if (!v || selected.has(norm(v))) return;
    onChange([...values, v]);
    setTxt("");
    setOpen(false);
    setHover(-1);
  };

  const removeAt = (idx) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (items[hover] || txt.trim())) {
      e.preventDefault();
      add(items[hover] ?? txt);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHover((h) => Math.min((h < 0 ? -1 : h) + 1, items.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setHover((h) => Math.max((h < 0 ? 0 : h) - 1, 0));
    }
    if (e.key === "Backspace" && !txt && values.length) {
      e.preventDefault();
      removeAt(values.length - 1);
    }
  };

  return (
    <div ref={ref} className="w-full">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-white px-2 py-2 focus-within:ring-2 focus-within:ring-[#0488AF]">
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className={CHIP_TAG}>
            {v}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="w-5 h-5 grid place-items-center rounded-full hover:bg-gray-200"
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[140px] border-0 outline-none bg-transparent py-1 px-2 text-sm"
          value={txt}
          onChange={(e) => {
            setTxt(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (txt.trim()) add(txt);
          }}
          placeholder={placeholder}
        />
      </div>

      {open && items.length > 0 && (
        <div className="relative">
          <div className="absolute z-20 mt-1 w-full rounded-xl border bg-white shadow-lg max-h-56 overflow-auto">
            {items.map((opt, idx) => (
              <button
                key={opt}
                type="button"
                onMouseEnter={() => setHover(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(opt)}
                className={`w-full text-left px-3 py-2 text-sm ${
                  idx === hover ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========= Modal crear/editar ========= */
function MinuteModal({
  open,
  mode = "create",
  initial,
  onClose,
  onSaved,
  tagSuggestions = [],
}) {
  const [title, setTitle] = useState(initial?.title || "");
  const [meetingDate, setMeetingDate] = useState(
    toYMDForInput(initial?.meetingDate) || ""
  );
  const [location, setLocation] = useState(initial?.location || "");
  const [summary, setSummary] = useState(initial?.summary || "");
  const [file, setFile] = useState(null);

  const [entityOptions, setEntityOptions] = useState([]);
  const [participants, setParticipants] = useState(
    (initial?.participants || []).map((p) => ({
      entityName: p.entityName,
      entityId: p.entityId,
    }))
  );
  const [tags, setTags] = useState(
    (initial?.tags || []).map((t) => t.tag || t)
  );

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const list = await listEntitiesForSuggestions();
        const arr = Array.isArray(list) ? list : [];
        setEntityOptions(arr.map((e) => ({ id: e.id, name: e.name })));
      } catch {
        // silencioso
      }
    })();
  }, [open]);

  const entityNameSuggestions = useMemo(
    () => entityOptions.map((o) => o.name),
    [entityOptions]
  );

  const canSave = title.trim() && meetingDate && (mode === "edit" || !!file);

  const handleSubmit = async () => {
    if (!canSave || saving) return;

    // Validar que la fecha no sea futura (solo fecha actual hacia atrás)
    if (meetingDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = ymdToUTCDate(meetingDate);
      if (selectedDate) {
        // Normalizar la fecha seleccionada a medianoche UTC para comparación
        const selectedDateNormalized = new Date(Date.UTC(
          selectedDate.getUTCFullYear(),
          selectedDate.getUTCMonth(),
          selectedDate.getUTCDate()
        ));
        const todayNormalized = new Date(Date.UTC(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        ));
        if (selectedDateNormalized > todayNormalized) {
          await Swal.fire({
            icon: "warning",
            title: "Fecha inválida",
            text: "No se puede subir una minuta con fecha futura. Solo se permiten fechas de hoy hacia atrás.",
          });
          return;
        }
      }
    }

    if (file && file.size > 15 * 1024 * 1024) {
      await Swal.fire({
        icon: "warning",
        title: "Archivo muy grande",
        text: "Máx 15 MB. Solo PDF.",
      });
      return;
    }

    const payload = {
      title: title.trim(),
      meetingDate: meetingDate,
      // Si tu API exige ISO:
      // meetingDate: ymdToUTCDate(meetingDate)?.toISOString(),
      location: location.trim(),
      summary: summary.trim(),
      participants,
      tags,
      ...(file ? { file } : {}),
    };

    try {
      setSaving(true);
      Swal.fire({
        title: mode === "edit" ? "Guardando…" : "Creando minuta…",
        html: "Por favor espera un momento.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      if (mode === "edit") {
        await updateMinute(initial.id, payload);
      } else {
        await createMinute(payload);
      }

      Swal.close();
      await Swal.fire({
        icon: "success",
        title: mode === "edit" ? "Minuta actualizada" : "Minuta creada",
        timer: 1100,
        showConfirmButton: false,
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: mode === "edit" ? "No se pudo actualizar" : "Error al crear",
        text: e?.message || "",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[260] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white">
          <h3 className="text-lg font-semibold break-words">
            {mode === "edit" ? "Editar minuta" : "Nueva minuta"}
          </h3>
          <p className="text-sm opacity-90">
            Banco de minutas de articulación público-privada.
          </p>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <Label>
              Título <span className="text-red-500">*</span>
            </Label>
            <input
              className="w-full rounded-lg border p-2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Minuta reunión Comité de Innovación"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label>
                  Fecha de reunión <span className="text-red-500">*</span>
                </Label>
              </div>
              <input
                type="date"
                className="w-full rounded-lg border p-2"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                Ubicación (opcional)
                <TooltipHelper text="Indica el lugar donde se realizó la reunión. Puedes buscar minutas por ubicación usando el campo de búsqueda principal." />
              </Label>
              <input
                className="w-full rounded-lg border p-2"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Ej: La Serena, Coquimbo, Santiago"
              />
            </div>
          </div>

          <div>
            <Label>Resumen (opcional)</Label>
            <textarea
              rows={3}
              className="w-full rounded-lg border p-2"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Describe brevemente los temas tratados, acuerdos principales, etc."
            />
          </div>

          <div>
            <Label className="flex items-center gap-1">
              Entidades participantes
              <TooltipHelper text="Escribe el nombre de la entidad y presiona Enter. Si el nombre coincide con una entidad del Directorio, se vinculará automáticamente. Puedes agregar múltiples entidades participantes." />
            </Label>
            <div className="w-full">
              <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-white px-2 py-2">
                {participants.map((p, i) => (
                  <span key={`${p.entityName}-${i}`} className={CHIP_ENTITY}>
                    {p.entityName}
                    <button
                      type="button"
                      onClick={() =>
                        setParticipants((arr) =>
                          arr.filter((_, idx) => idx !== i)
                        )
                      }
                      className="grid place-items-center w-5 h-5 rounded-full hover:bg-indigo-100"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  list="minute-entities-suggest"
                  className="flex-1 min-w-[160px] border-0 outline-none bg-transparent py-1 px-2 text-sm"
                  placeholder="CORFO, SEREMI…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const v = e.currentTarget.value.trim();
                      if (v) {
                        const match = entityNameSuggestions.find(
                          (n) => norm(n) === norm(v)
                        );
                        const found = match
                          ? entityOptions.find((o) => o.name === match)
                          : null;
                        setParticipants((arr) => [
                          ...arr,
                          match
                            ? { entityName: match, entityId: found?.id }
                            : { entityName: v },
                        ]);
                        e.currentTarget.value = "";
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const v = e.currentTarget.value.trim();
                    if (v) {
                      const match = entityNameSuggestions.find(
                        (n) => norm(n) === norm(v)
                      );
                      const found = match
                        ? entityOptions.find((o) => o.name === match)
                        : null;
                      setParticipants((arr) => [
                        ...arr,
                        match
                          ? { entityName: match, entityId: found?.id }
                          : { entityName: v },
                      ]);
                      e.currentTarget.value = "";
                    }
                  }}
                />
                <datalist id="minute-entities-suggest">
                  {entityNameSuggestions.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1">
              Etiquetas
              <TooltipHelper text="Agrega etiquetas para categorizar la minuta. Escribe y presiona Enter, o selecciona de las sugerencias. Las etiquetas ayudan a filtrar y encontrar minutas relacionadas con temas específicos." />
            </Label>
            <ChipsAuto
              values={tags}
              onChange={setTags}
              suggestions={tagSuggestions}
              placeholder="innovación, fomento, convenio…"
            />
          </div>

          <div>
            <Label className="flex items-center gap-2">
              {mode === "edit" ? "Reemplazar PDF (opcional)" : "Archivo PDF *"}
              {mode === "edit" ? (
                <TooltipHelper text="Si deseas actualizar el PDF de la minuta, selecciona un nuevo archivo. El archivo anterior será reemplazado. Si no seleccionas un archivo, se mantendrá el PDF actual. Máximo 15 MB, solo formato PDF." />
              ) : (
                <TooltipHelper text="El archivo PDF es obligatorio para crear una nueva minuta. Asegúrate de que el archivo esté completo y sea legible." />
              )}
            </Label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center cursor-pointer">
                <span className="rounded-md border-0 bg-[#0488AF] px-4 py-2 text-white text-sm hover:bg-[#037a98] transition-colors">
                  Seleccionar archivo
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={async (e) => {
                    const selectedFile = e.target.files?.[0] || null;
                    if (selectedFile) {
                      if (selectedFile.type !== 'application/pdf') {
                        await Swal.fire({
                          icon: 'error',
                          title: 'Formato inválido',
                          text: 'Debes subir un archivo PDF',
                        });
                        e.target.value = '';
                        return;
                      }
                      if (selectedFile.size > 15 * 1024 * 1024) {
                        await Swal.fire({
                          icon: 'warning',
                          title: 'Archivo muy grande',
                          html: `El archivo "${selectedFile.name}" es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
                          confirmButtonText: 'Entendido',
                        });
                        e.target.value = '';
                        return;
                      }
                    }
                    setFile(selectedFile);
                  }}
                  className="hidden"
                />
              </label>
              {mode === "edit" && (
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {file ? file.name : "Ningún archivo de reemplazo seleccionado"}
                </span>
              )}
              {mode !== "edit" && (
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {file ? file.name : "Máx 15 MB. Solo PDF."}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex justify-end gap-2 bg-white">
          <button className={BTN_OUTLINE} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={
              canSave && !saving
                ? BTN_PRIMARY
                : `${BTN_BASE} bg-gray-400 cursor-not-allowed text-white`
            }
            onClick={handleSubmit}
            disabled={!canSave || saving}
          >
            {saving
              ? mode === "edit"
                ? "Guardando…"
                : "Subiendo…"
              : mode === "edit"
              ? "Guardar cambios"
              : "Crear minuta"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =============== Página principal =============== */
export default function Minutes() {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // filtros enviados a API
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // filtros locales
  const [entityLike, setEntityLike] = useState("");
  const [tagChips, setTagChips] = useState([]);
  const [descriptionSearch, setDescriptionSearch] = useState("");

  // vista - solo lista
  const view = "list";

  // modal
  const [modal, setModal] = useState(null);

  // id en eliminación
  const [deletingId, setDeletingId] = useState(null);

  async function refresh(overrideParams) {
    try {
      setLoading(true);
      const params = overrideParams ?? {};
      // Solo incluir parámetros que tengan valor
      // Si overrideParams tiene una propiedad, usarla; si no, usar el estado
      if (overrideParams?.q !== undefined) {
        if (overrideParams.q) params.q = overrideParams.q;
      } else if (q) {
        params.q = q;
      }
      
      if (overrideParams?.from !== undefined) {
        if (overrideParams.from) params.from = overrideParams.from;
      } else if (from) {
        params.from = from;
      }
      
      if (overrideParams?.to !== undefined) {
        if (overrideParams.to) params.to = overrideParams.to;
      } else if (to) {
        params.to = to;
      }
      
      const data = await listMinutes(params);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "No se pudieron cargar las minutas",
        text: e?.message || "",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh(); // carga inicial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entityFilterSuggestions = useMemo(() => {
    const s = new Set();
    items.forEach((m) =>
      (m.participants || []).forEach((p) => {
        const name = (p.entityName || "").trim();
        if (name) s.add(name);
      })
    );
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((m) =>
      (m.tags || []).forEach((t) => s.add(t.tag || t))
    );
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [items]);

  // Filtro en memoria: entidad, etiquetas y búsqueda por título/ubicación/descripción (case-insensitive)
  const filtered = useMemo(() => {
    const entityNeedle = norm(entityLike);
    const qNeedle = norm(q);
    const descNeedle = norm(descriptionSearch);

    return items.filter((m) => {
      if (qNeedle) {
        // Búsqueda case-insensitive en título y ubicación
        const titleMatch = norm(m.title || "").includes(qNeedle);
        const locationMatch = norm(m.location || "").includes(qNeedle);
        if (!titleMatch && !locationMatch) return false;
      }

      if (descNeedle) {
        // Búsqueda case-insensitive en descripción (summary)
        const summaryMatch = norm(m.summary || "").includes(descNeedle);
        if (!summaryMatch) return false;
      }

      if (entityNeedle) {
        const entityOK = m?.participants?.some((p) =>
          norm(p.entityName).includes(entityNeedle)
        );
        if (!entityOK) return false;
      }

      if (tagChips.length > 0) {
        const tagSet = new Set(
          (m.tags || []).map((t) => norm(t.tag || t))
        );
        const tagsOK = tagChips.every((t) => tagSet.has(norm(t)));
        if (!tagsOK) return false;
      }

      return true;
    });
  }, [items, entityLike, tagChips, q, descriptionSearch]);

  const onDelete = async (minute) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar minuta",
      text: `¿Seguro que deseas eliminar la minuta "${minute.title}"? Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#B91C1C",
    });
    if (!res.isConfirmed) return;

    try {
      setDeletingId(minute.id);
      Swal.fire({
        title: "Eliminando…",
        html: "Por favor espera un momento.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      await deleteMinute(minute.id);

      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "Minuta eliminada",
        timer: 1000,
        showConfirmButton: false,
      });
      await refresh();
    } catch (e) {
      Swal.close();
      await Swal.fire({
        icon: "error",
        title: "No se pudo eliminar",
        text: e?.message || "",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const clearFilters = () => {
    setQ("");
    setFrom("");
    setTo("");
    setEntityLike("");
    setTagChips([]);
    setDescriptionSearch("");
    // Pasar explícitamente valores vacíos para forzar la limpieza
    refresh({ q: "", from: "", to: "" });
  };

  const openPdf = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  /* ======= Fila (LISTA) ======= */
  const MinuteRow = ({ m }) => {
    const deleting = deletingId === m.id;
    const url = m.viewUrl || m.fileSecureUrl || m.fileUrl;

    return (
      <div className="group border border-gray-200 rounded-xl bg-white px-5 py-4 hover:shadow-sm transition-all">
        <div className="grid grid-cols-[56px_1fr] sm:grid-cols-[56px_1fr_auto] gap-4 items-start">
          <div className="shrink-0 w-[56px] h-[56px] rounded-lg border grid place-items-center bg-gradient-to-br from-blue-50 to-white">
            <PdfIcon size={32} />
          </div>

          <div className="min-w-0">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#0B4A8F] hover:underline truncate break-words"
                title={m.title}
              >
                {m.title}
              </a>
            ) : (
              <span
                className="font-semibold text-[#0B4A8F] truncate break-words"
                title={m.title}
              >
                {m.title}
              </span>
            )}

            {/* meta: fecha + ubicación */}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                <span>{formatDateSafe(m.meetingDate)}</span>
              </span>
              {m.location && (
                <span className={LOCATION_BADGE}>
                  <MapPin className="w-3 h-3" />
                  <span>{m.location}</span>
                </span>
              )}
            </div>

            {m.summary && (
              <p className="text-sm text-gray-700 mt-1 line-clamp-2 break-words">
                {m.summary}
              </p>
            )}

            {/* Títulos para entidades y etiquetas */}
            <div className="mt-2 space-y-1">
              {(m.participants || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Entidades:
                  </span>
                  {(m.participants || []).map((p) => (
                    <button
                      key={`${p.id || p.entityName}`}
                      onClick={() => setEntityLike(p.entityName)}
                      className={CHIP_ENTITY}
                      title="Filtrar por esta entidad"
                    >
                      {p.entityName}
                    </button>
                  ))}
                </div>
              )}

              {(m.tags || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Etiquetas:
                  </span>
                  {(m.tags || []).map((t) => (
                    <button
                      key={t.id || t.tag}
                      onClick={() =>
                        setTagChips((prev) =>
                          prev.includes(t.tag) ? prev : [...prev, t.tag]
                        )
                      }
                      className={CHIP_TAG}
                      title="Filtrar por esta etiqueta"
                    >
                      {t.tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="sm:justify-self-end flex flex-wrap gap-2 mt-2 sm:mt-0">
            {/* Ver PDF */}
            <button
              className={`${BTN_OUTLINE} disabled:opacity-60 disabled:cursor-not-allowed`}
              onClick={() => openPdf(url)}
              disabled={!url}
              title={url ? "Abrir PDF" : "Sin archivo"}
            >
              <Eye className="w-4 h-4" />
            </button>

            {isAdmin && (
              <>
                <button
                  className={BTN_OUTLINE}
                  onClick={() => setModal({ mode: "edit", values: m })}
                >
                  Editar
                </button>
                <button
                  className={`${BTN_DANGER} disabled:opacity-60 disabled:cursor-not-allowed`}
                  onClick={() => onDelete(m)}
                  disabled={deleting}
                >
                  {deleting ? "Eliminando…" : "Eliminar"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ======= Tarjeta (GRID) ======= */
  const MinuteCard = ({ m }) => {
    const deleting = deletingId === m.id;
    const url = m.viewUrl || m.fileSecureUrl || m.fileUrl;

    return (
      <div className="group rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-all">
        <div className="p-4 flex gap-4">
          <div className="shrink-0 w-[80px] h-[80px] rounded-xl border grid place-items-center bg-gradient-to-br from-blue-50 to-white">
            <PdfIcon />
          </div>
          <div className="min-w-0 flex-1">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-extrabold text-[#0B4A8F] text-lg hover:underline truncate break-words"
                title={m.title}
              >
                {m.title}
              </a>
            ) : (
              <span
                className="font-extrabold text-[#0B4A8F] text-lg truncate break-words"
                title={m.title}
              >
                {m.title}
              </span>
            )}

            {/* meta: fecha + ubicación */}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" />
                <span>{formatDateSafe(m.meetingDate)}</span>
              </span>
              {m.location && (
                <span className={LOCATION_BADGE}>
                  <MapPin className="w-3 h-3" />
                  <span>{m.location}</span>
                </span>
              )}
            </div>

            {m.summary && (
              <p className="text-sm text-gray-700 mt-2 line-clamp-3 break-words">
                {m.summary}
              </p>
            )}

            <div className="mt-2 space-y-1">
              {(m.participants || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Entidades:
                  </span>
                  {(m.participants || []).map((p) => (
                    <button
                      key={`${p.id || p.entityName}`}
                      onClick={() => setEntityLike(p.entityName)}
                      className={`${CHIP_ENTITY} mt-1`}
                    >
                      {p.entityName}
                    </button>
                  ))}
                </div>
              )}

              {(m.tags || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Etiquetas:
                  </span>
                  {(m.tags || []).map((t) => (
                    <button
                      key={t.id || t.tag}
                      onClick={() =>
                        setTagChips((prev) =>
                          prev.includes(t.tag) ? prev : [...prev, t.tag]
                        )
                      }
                      className={`${CHIP_TAG} mt-1`}
                    >
                      {t.tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t bg-white flex flex-wrap gap-2">
          <button
            className={`${BTN_OUTLINE} disabled:opacity-60 disabled:cursor-not-allowed`}
            onClick={() => openPdf(url)}
            disabled={!url}
            title={url ? "Abrir PDF" : "Sin archivo"}
          >
            <Eye className="w-4 h-4" />
          </button>

          {isAdmin && (
            <>
              <button
                className={BTN_OUTLINE}
                onClick={() => setModal({ mode: "edit", values: m })}
              >
                Editar
              </button>
              <button
                className={`${BTN_DANGER} disabled:opacity-60 disabled:cursor-not-allowed`}
                onClick={() => onDelete(m)}
                disabled={deleting}
              >
                {deleting ? "Eliminando…" : "Eliminar"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[70vh]">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Banner principal ahora full width, mismo ancho que los GradientCard */}
        <div className="relative">
          <div className="px-1 pb-2">
            <ArrowBack />
          </div>
          <Banner
            title="Banco de minutas"
            message="Registra, etiqueta y consulta minutas de reuniones y actividades."
            className="w-full mb-4"
          />
        </div>

        <SectionTips
          section="el banco de minutas"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar el banco de minutas?"
          tipsList={[
            'En esta sección puedes registrar y revisar minutas de diferentes tipos de reuniones y eventos.',
            'En las minutas puedes registrar información detallada como participantes, ubicación, etiquetas y adjuntar un archivo.',
            'En la lista de minutas, haz clic en el nombre de la minuta o en el símbolo de ojo para abrir el archivo adjunto y descargarlo.',
            'Utiliza los filtros para buscar minutas por diferentes atributos, puedes filtrar por más de una etiqueta a la vez presionando Enter (recuerda presionar "Filtrar" para actualizar la búsqueda).',
          ]}
          finalNote="Consejo: Etiqueta tus minutas correctamente para facilitar la búsqueda."
          color="#0488AF"
          videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766417251/Video_Tutorial_m%C3%B3dulo_Articulaci%C3%B3n_P-P_-_Banco_de_Minutas_kdkhj6.mp4"
          manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412940/Manual_de_usuario_m%C3%B3dulo_de_articulaci%C3%B3n_P-P_nfoqgo.pdf"
        />

        {/* Filtros con borde degradado */}
        <GradientCard className="mb-3">
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 items-end">
              <div className="lg:col-span-2">
                <Label>Buscar por título o ubicación</Label>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="w-full rounded-2xl border px-4 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none"
                  placeholder="Ej: Comité de Innovación, La Serena…"
                />
              </div>

              <div className="lg:col-span-2">
                <Label>Buscar por descripción</Label>
                <input
                  value={descriptionSearch}
                  onChange={(e) => setDescriptionSearch(e.target.value)}
                  className="w-full rounded-2xl border px-4 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none"
                  placeholder="Ej: acuerdos, temas tratados…"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Desde</Label>
                  {from && (
                    <button
                      type="button"
                      onClick={() => setFrom("")}
                      className="text-[11px] text-[#0488AF] hover:underline"
                    >
                      Limpiar fecha
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="w-full rounded-2xl border px-4 py-2"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Hasta</Label>
                  {to && (
                    <button
                      type="button"
                      onClick={() => setTo("")}
                      className="text-[11px] text-[#0488AF] hover:underline"
                    >
                      Limpiar fecha
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="w-full rounded-2xl border px-4 py-2"
                />
              </div>

              <div>
                <Label>Filtrar por Entidad</Label>
                <AutocompleteFilter
                  value={entityLike}
                  onChange={setEntityLike}
                  options={entityFilterSuggestions}
                  placeholder="CORFO, SEREMI…"
                />
              </div>

              <div className="lg:col-span-2">
                <Label>Filtrar por etiquetas</Label>
                <ChipsAuto
                  values={tagChips}
                  onChange={setTagChips}
                  suggestions={allTags}
                />
              </div>

              <div className="flex gap-2 lg:col-span-6 items-center flex-wrap">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchIcon />}
                  onClick={() => refresh()}
                  sx={{
                    background: 'linear-gradient(to right, #09274A, #0488AF)',
                    color: '#fff',
                    textTransform: 'none',
                    minWidth: 140,
                    minHeight: '40px',
                    padding: '6px 16px',
                    lineHeight: '1.5',
                    '&:hover': {
                      background: 'linear-gradient(to right, #071f3d, #03779C)',
                    },
                  }}
                >
                  Filtrar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={clearFilters}
                  sx={{
                    borderColor: '#DC3465',
                    color: '#DC3465',
                    textTransform: 'none',
                    minWidth: 140,
                    minHeight: 40,
                    padding: '6px 16px',
                    lineHeight: '1.5',
                    '&:hover': {
                      backgroundColor: '#C42251',
                      color: 'white',
                    },
                  }}
                >
                  Limpiar
                </Button>
                {/* Instrucción informativa */}
                <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm">
                  <span className="text-base leading-none">ℹ️</span>
                  <span>
                    Para actualizar los resultados debes presionar el botón{" "}
                    <span className="font-bold text-blue-900">"Filtrar"</span>.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </GradientCard>

        {/* Botón Nueva minuta y contador en la misma barra */}
        <GradientCard className="mb-4">
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-4">
            {/* Contador de minutas */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0488AF]/20 to-[#0488AF]/10 flex items-center justify-center text-[#0488AF] border border-[#0488AF]/20">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-0.5">
                  Contador de minutas
                </p>
                <p className="text-sm font-medium text-slate-800">
                  {items.length === 0
                    ? "No hay minutas registradas todavía."
                    : filtered.length === items.length
                    ? `Mostrando ${filtered.length} minutas.`
                    : `Mostrando ${filtered.length} de ${items.length} minutas.`}
                </p>
              </div>
            </div>

            {/* Botón Nueva minuta */}
            {isAdmin && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PostAddIcon />}
                onClick={() => setModal({ mode: "create" })}
                sx={{
                  background: 'linear-gradient(to right, #09274A, #0488AF)',
                  color: '#fff',
                  textTransform: 'none',
                  minWidth: 140,
                  minHeight: '40px',
                  padding: '6px 16px',
                  lineHeight: '1.5',
                  '&:hover': {
                    background: 'linear-gradient(to right, #071f3d, #03779C)',
                  },
                }}
              >
                Nueva minuta
              </Button>
            )}
          </div>
        </GradientCard>

        {/* contenido con borde degradado */}
        {loading ? (
          <GradientCard>
            <div className="p-8 text-center text-gray-500">Cargando…</div>
          </GradientCard>
        ) : filtered.length === 0 ? (
          <GradientCard>
            <div className="p-8 text-gray-500">No hay minutas.</div>
          </GradientCard>
        ) : (
          <GradientCard>
            <div className="p-4 space-y-3">
              {filtered.map((m) => (
                <MinuteRow key={m.id} m={m} />
              ))}
            </div>
          </GradientCard>
        )}

        {/* Modal */}
        {modal && (
          <MinuteModal
            open={!!modal}
            mode={modal.mode}
            initial={modal.values}
            onClose={() => setModal(null)}
            onSaved={refresh}
            tagSuggestions={allTags}
          />
        )}
      </div>
    </div>
  );
}
